import React, { ReactElement, useState, useEffect, useContext } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Skeleton,
    Divider,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    Person as PersonIcon,
    TrendingUp as TrendingUpIcon,
    Group as GroupIcon,
    Visibility as VisibilityIcon,
    SportsEsports as SportsEsportsIcon,
    Link as LinkIcon
} from '@mui/icons-material';
import { UserContext } from '../../contexts/UserContext';
import { HttpClient } from '../../api/HttpClient';
import { UserDetailsResponse, ActiveRoomInfo, PlanType, PlanStatus } from '../../api/models/User';

interface UserDetailModalProps {
    open: boolean;
    onClose: () => void;
    userId: string | null;
}

export function UserDetailModal({ open, onClose, userId }: UserDetailModalProps): ReactElement {
    const { authToken } = useContext(UserContext);
    const [userDetails, setUserDetails] = useState<UserDetailsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && userId && authToken) {
            fetchUserDetails();
        }
    }, [open, userId, authToken]);

    const fetchUserDetails = async () => {
        if (!userId || !authToken) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const httpClient = new HttpClient(authToken);
            const details = await httpClient.get<UserDetailsResponse>(`/admin/users/${userId}/details`);
            setUserDetails(details);
        } catch (err) {
            console.error('Failed to fetch user details:', err);
            setError('Failed to load user details');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setUserDetails(null);
        setError(null);
        onClose();
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'Never';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Invalid date';
        }
    };

    const getPlanTypeColor = (planType: PlanType) => {
        switch (planType) {
            case PlanType.Premium: return 'success';
            case PlanType.Trial: return 'warning';
            case PlanType.Free: return 'default';
            default: return 'default';
        }
    };

    const getPlanStatusColor = (planStatus: PlanStatus) => {
        switch (planStatus) {
            case PlanStatus.Active: return 'success';
            case PlanStatus.Canceled: return 'error';
            case PlanStatus.Expired: return 'warning';
            default: return 'default';
        }
    };

    const getRoomStateColor = (state: string) => {
        switch (state) {
            case 'playing': return 'success';
            case 'waiting_for_players': return 'warning';
            case 'result': return 'info';
            case 'not_started': return 'default';
            case 'suspended': return 'error';
            default: return 'default';
        }
    };

    const copyRoomLink = (urlId: string) => {
        const roomUrl = `${window.location.origin}/${urlId}`;
        navigator.clipboard.writeText(roomUrl);
    };

    const renderRoomRow = (room: ActiveRoomInfo) => (
        <TableRow key={room.roomId} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
            <TableCell sx={{ py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {room.urlId}
                    </Typography>
                    <Tooltip title="Copy room link">
                        <IconButton 
                            size="small" 
                            onClick={() => copyRoomLink(room.urlId)}
                            sx={{ p: 0.5 }}
                        >
                            <LinkIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </TableCell>
            <TableCell sx={{ py: 1.5 }}>
                <Chip 
                    label={room.state.replace('_', ' ')} 
                    color={getRoomStateColor(room.state)}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                />
            </TableCell>
            <TableCell sx={{ py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {room.playerName}
                    </Typography>
                    {room.isModerator && (
                        <Tooltip title={room.playerName + " is moderator of this room"} aria-label="moderator">
                            <SportsEsportsIcon sx={{ marginLeft: 1, color: 'icon.main' }} />
                        </Tooltip>
                    )}
                    {room.isObserver && (
                        <Tooltip title={room.playerName + " is just observing this game"} aria-label="observer">
                            <VisibilityIcon sx={{ marginLeft: room.isModerator ? 1 : 0, color: 'icon.main' }} />
                        </Tooltip>
                    )}
                </Box>
            </TableCell>
            <TableCell sx={{ py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <GroupIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                        {room.totalPlayers}
                    </Typography>
                </Box>
            </TableCell>
            <TableCell sx={{ py: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {room.estimationRounds}
                </Typography>
            </TableCell>
            <TableCell sx={{ py: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                    {formatDate(room.lastSeenAt)}
                </Typography>
            </TableCell>
            <TableCell sx={{ py: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                    {formatDate(room.roomUpdatedAt)}
                </Typography>
            </TableCell>
        </TableRow>
    );

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    minHeight: '60vh'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                pb: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" />
                    <Typography variant="h6">
                        User Details
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 2 }}>
                {loading ? (
                    <Box sx={{ py: 2 }}>
                        <Skeleton variant="text" width="60%" height={32} />
                        <Skeleton variant="text" width="40%" height={24} sx={{ mt: 1 }} />
                        <Skeleton variant="rectangular" height={200} sx={{ mt: 2, borderRadius: 1 }} />
                    </Box>
                ) : error ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography color="error" variant="body1">
                            {error}
                        </Typography>
                        <Button onClick={fetchUserDetails} sx={{ mt: 2 }}>
                            Retry
                        </Button>
                    </Box>
                ) : userDetails ? (
                    <Box>
                        {/* User Information */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon fontSize="small" />
                                User Information
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        Email
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {userDetails.email}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        Plan Type
                                    </Typography>
                                    <Chip 
                                        label={PlanType[userDetails.planType]} 
                                        color={getPlanTypeColor(userDetails.planType)}
                                        size="small"
                                        sx={{ textTransform: 'capitalize' }}
                                    />
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        Plan Status
                                    </Typography>
                                    <Chip 
                                        label={PlanStatus[userDetails.planStatus]} 
                                        color={getPlanStatusColor(userDetails.planStatus)}
                                        size="small"
                                        sx={{ textTransform: 'capitalize' }}
                                    />
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        Plan Start Date
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatDate(userDetails.planStartDate)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        Last Sign In
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatDate(userDetails.lastSignInAt)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        Account Created
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatDate(userDetails.createdAt)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Active Rooms */}
                        <Box>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrendingUpIcon fontSize="small" />
                                Active Rooms ({userDetails.activeRooms.length})
                            </Typography>
                            
                            {userDetails.activeRooms.length === 0 ? (
                                <Box sx={{ py: 4, textAlign: 'center' }}>
                                    <Typography color="text.secondary" variant="body1">
                                        No active rooms found
                                    </Typography>
                                </Box>
                            ) : (
                                <TableContainer component={Paper} sx={{ borderRadius: 1, boxShadow: 1 }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                                <TableCell sx={{ fontWeight: 600 }}>Room ID</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Player Name</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Players</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Estimates</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Last Seen</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Last Updated</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {userDetails.activeRooms.map(renderRoomRow)}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Box>
                ) : null}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
